import { useAuth } from '@clerk/clerk-react';
import { useEffect, useCallback, useRef, ReactNode } from 'react';
import { setSupabaseTokenProvider } from '@cleanpro/common-ui';

interface SupabaseAuthSyncProps {
  children: ReactNode;
}

/**
 * Synchronizes Clerk authentication with Supabase client
 * Following official Clerk + Supabase integration pattern:
 * https://clerk.com/docs/guides/development/integrations/databases/supabase
 * 
 * This sets up a token provider that injects Clerk's JWT into every Supabase request,
 * allowing RLS policies to access user/org data via auth.jwt()
 */
export function SupabaseAuthSync({ children }: SupabaseAuthSyncProps) {
  const { getToken } = useAuth();
  const initializedRef = useRef(false);

  const tokenGetter = useCallback(async () => {
    try {
      return await getToken({ template: 'supabase' });
    } catch (error) {
      console.error('❌ Error getting Clerk token:', error);
      return null;
    }
  }, [getToken]);

  useEffect(() => {
    // Only initialize once to avoid duplicate provider setup
    if (!initializedRef.current) {
      setSupabaseTokenProvider(tokenGetter);
      initializedRef.current = true;
    }
  }, [tokenGetter]);

  return <>{children}</>;
}
